import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { EditSolidIcon, KeyIcon } from '@dhl-ui/icons'
import { useGetOrganisationCCCApiCredentialsHook } from '@dhl/mdp-api'
import { Box, Card, Checkbox, Divider, FormGroup, Grid, H6, IconButton } from '@dhl-ui/components'
import { DescriptionListItem } from '~/ui-lib/DescriptionList'
import { links } from '~/AppRoutes'
import { config } from '~/config'
import { isBeNlBusinessUnit, isCccBusinessUnit, isPplCzBusinessUnit } from '~/common/model/businessunits'

import { APICredentialsDialog, type ApiCredentialsDialogData } from '~/common/components/APICredentialsDialog'
import { COUNTRIES } from '~/common/config/country'
import { useNavigateWithSearchString, useNotificationMessage } from '~/common/hooks'
import { useBillingUnits } from '~/modules/billingunits/hooks'

import type { Organisation } from '~/modules/organisations/model'

type Props = {
  organisation: Organisation
}

export const OrganisationDetails = ({ organisation }: Props) => {
  const { disableCccOrgCrud } = config
  const { t } = useTranslation()
  const navigateWithSearchString = useNavigateWithSearchString(true)
  const { showError } = useNotificationMessage()
  const isCcc = isCccBusinessUnit(organisation.businessUnitCode)
  const isPplCz = isPplCzBusinessUnit(organisation.businessUnitCode)
  const { id } = organisation
  const [apiCredentialsData, setApiCredentialsData] = useState<ApiCredentialsDialogData | undefined>()
  const { billingUnits } = useBillingUnits()
  const getOrganisationCCCApiCredentials = useGetOrganisationCCCApiCredentialsHook()

  const filteredCountry = COUNTRIES?.filter(({ value }) => {
    return value === organisation.data.address?.countryCode
  })

  const filteredBillingUnit = billingUnits.filter((billingUnit) => {
    return billingUnit.id === organisation.ccc?.billingUnitId
  })

  const generateApiCredentials = async () => {
    try {
      const response = await getOrganisationCCCApiCredentials(id)
      setApiCredentialsData({
        id,
        clientId: response.clientId,
        clientSecret: response.clientSecret,
      })
    } catch (error) {
      if (isAxiosError(error)) {
        showError(error.message)
      }
    }
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.organisationsPage.organisationForm.titleText')}
          endNode={
            <>
              <Grid container direction="row" spacing={1}>
                {isCcc && !disableCccOrgCrud && (
                  <>
                    <Grid>
                      <IconButton aria-label="generate credentials" onClick={() => generateApiCredentials()}>
                        <KeyIcon />
                      </IconButton>
                    </Grid>
                    <Grid>
                      <Divider orientation="vertical" />
                    </Grid>
                  </>
                )}
                {id && (
                  <Grid>
                    <IconButton
                      aria-label="edit organisation"
                      onClick={() => navigateWithSearchString(links.editOrganisationLink(id))}
                    >
                      <EditSolidIcon data-testid="edit-organisation" />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </>
          }
        />
        <Card.Content>
          <Box mb={2}>
            <H6>{t('admin.organisationsPage.organisationForm.accountInformation')}</H6>
          </Box>

          <Grid direction="row" container spacing={1} component="dl">
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.company')}
                description={organisation.name}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.businessUnit')}
                description={organisation.businessUnitCode}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t(`admin.organisationsPage.organisationForm.${isPplCz ? 'icoNumber' : 'COCNumber'}`)}
                description={isPplCz ? organisation.data.icoNumber : organisation.data.chamberOfCommerce}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.vatNumber')}
                description={organisation.data.vatNumber}
              />
            </Grid>
          </Grid>
          {isCcc && (
            <>
              <Box mb={2} mt={3}>
                <H6>{t('admin.organisationsPage.organisationForm.CCCCustomerInformation')}</H6>
              </Box>
              <Grid direction="row" container spacing={1} component="dl">
                <Grid size={{ sm: 6 }}>
                  <DescriptionListItem
                    title={t('admin.organisationsPage.organisationForm.customerID')}
                    description={organisation.ccc?.customerId}
                  />
                </Grid>
                <Grid size={{ sm: 6 }}>
                  <DescriptionListItem
                    title={t('admin.organisationsPage.organisationForm.billingUnit')}
                    description={filteredBillingUnit && filteredBillingUnit[0]?.name}
                  />
                </Grid>
              </Grid>
            </>
          )}
          <Box mb={2} mt={3}>
            <H6>{t('admin.organisationsPage.organisationForm.addressInformation')}</H6>
          </Box>
          <Grid direction="row" container spacing={1} component="dl">
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.postalCode')}
                description={organisation.data.address?.postalCode}
              />
            </Grid>
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.number')}
                description={organisation.data.address?.houseNumber}
              />
            </Grid>
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.addition')}
                description={organisation.data.address?.houseNumberSuffix}
              />
            </Grid>
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.street')}
                description={organisation.data.address?.street}
              />
            </Grid>
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.city')}
                description={organisation.data.address?.city}
              />
            </Grid>
            <Grid size={{ sm: 4 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.country')}
                description={filteredCountry[0]?.label}
              />
            </Grid>
          </Grid>
          <Box mb={2} mt={3}>
            <H6>{t('admin.organisationsPage.organisationForm.contact')}</H6>
          </Box>
          <Grid direction="row" container spacing={1}>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.firstName')}
                description={organisation.data.contactPerson?.firstName}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.lastName')}
                description={organisation.data.contactPerson?.lastName}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.emailAddress')}
                description={organisation.data.contactPerson?.email}
              />
            </Grid>
            <Grid size={{ sm: 6 }}>
              <DescriptionListItem
                title={t('admin.organisationsPage.organisationForm.phoneNumber')}
                description={organisation.data.contactPerson?.phone}
              />
            </Grid>
          </Grid>
          {isBeNlBusinessUnit(organisation.businessUnitCode) && (
            <>
              <Box mb={2} mt={3}>
                <H6>{t('admin.organisationsPage.organisationForm.labelSettings')}</H6>
              </Box>
              <Grid direction="row" container spacing={1} component="dl">
                <Grid size={{ sm: 6 }}>
                  <DescriptionListItem
                    title={t('admin.organisationsPage.organisationForm.postboxNumber')}
                    description={organisation.data.labelSettings?.undeliverableReturnPostboxNumber}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Box mb={2} mt={3}>
            <H6>{t('admin.organisationsPage.organisationForm.otherOptions')}</H6>
          </Box>
          <Box mt={3}>
            <FormGroup>
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gridTemplateRows: 'repeat(4, auto)',
                  gap: 0.75,
                }}
              >
                <Checkbox
                  checked={organisation.useCompanyAddressBook}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.companyAddressBook')}
                />
                <Checkbox
                  checked={organisation.useSharedShipments}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.sharedShipments')}
                />
                <Checkbox
                  checked={organisation.useAdvancedParcelType}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.specifyWeight')}
                />
                <Checkbox
                  checked={organisation.useSharedTickets}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.sharedTickets')}
                />
                <Checkbox
                  checked={organisation.useSharedImportSchemas}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.sharedImportSchemas')}
                />
                <Checkbox
                  checked={organisation.useSharedDrafts}
                  disabled
                  label={t('admin.organisationsPage.organisationForm.sharedDrafts')}
                />
              </Box>
            </FormGroup>
          </Box>
        </Card.Content>
      </Card>
      <APICredentialsDialog
        open={!!apiCredentialsData}
        onClose={() => {
          setApiCredentialsData(undefined)
        }}
        {...apiCredentialsData}
      />
    </>
  )
}
